import { useCallback, useEffect, useState } from "react";

import { isCalendarView, shift, type CalendarView } from "./calendarModel";

export const DEFAULT_VIEW_KEY = "clientProfile.calendar.view";

const readView = (key: string, fallback: CalendarView): CalendarView => {
  try {
    const raw = window.localStorage.getItem(key);
    return isCalendarView(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
};

const writeView = (key: string, view: CalendarView) => {
  try {
    window.localStorage.setItem(key, view);
  } catch {
    // ignore
  }
};

const SHORTCUTS: Record<string, CalendarView> = {
  m: "month",
  w: "week",
  d: "day",
  a: "agenda"
};

/** True when a keypress should be left alone (typing or inside an overlay). */
const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(
    target.closest(
      '[role="dialog"], [role="menu"], [role="listbox"], [data-slot="popover-content"]'
    )
  );
};

export type CalendarState = {
  view: CalendarView;
  anchor: Date;
  setView: (view: CalendarView) => void;
  setAnchor: (date: Date) => void;
  today: () => void;
  prev: () => void;
  next: () => void;
};

/**
 * View + anchor date for the calendar. The view is remembered across visits;
 * `M W D A` switch views and `T` jumps to today (ignored while typing or when
 * an overlay is open).
 */
export const useCalendarState = (
  initialAnchor: Date = new Date(),
  {
    shortcuts = true,
    storageKey = DEFAULT_VIEW_KEY,
    defaultView = "month"
  }: {
    shortcuts?: boolean;
    /** localStorage key the chosen view is remembered under. */
    storageKey?: string;
    defaultView?: CalendarView;
  } = {}
): CalendarState => {
  const [view, setViewState] = useState<CalendarView>(() =>
    readView(storageKey, defaultView)
  );
  const [anchor, setAnchor] = useState<Date>(initialAnchor);

  const setView = useCallback(
    (next: CalendarView) => {
      setViewState(next);
      writeView(storageKey, next);
    },
    [storageKey]
  );

  const today = useCallback(() => setAnchor(new Date()), []);
  const prev = useCallback(() => setAnchor((a) => shift(view, a, -1)), [view]);
  const next = useCallback(() => setAnchor((a) => shift(view, a, 1)), [view]);

  useEffect(() => {
    if (!shortcuts) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (document.body.dataset.scrollLocked !== undefined) return;
      const key = event.key.toLowerCase();
      if (key === "t") {
        event.preventDefault();
        today();
        return;
      }
      const target = SHORTCUTS[key];
      if (target) {
        event.preventDefault();
        setView(target);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setView, shortcuts, today]);

  return { view, anchor, setView, setAnchor, today, prev, next };
};
